<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="ncrm-company-email-send-drawer__overlay"
      @click.self="handleModelUpdate(false)"
    >
      <aside
        class="ncrm-company-email-send-drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('ncrm.integrations.companyEmail.send.title')"
      >
        <div class="ncrm-company-email-send-drawer__content">
      <div class="d-flex align-start justify-space-between ga-3">
        <div>
          <div class="text-h6">{{ $t("ncrm.integrations.companyEmail.send.title") }}</div>
          <div class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.integrations.companyEmail.send.subtitle") }}
          </div>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          :aria-label="$t('app.actions.close')"
          :disabled="submitting"
          @click="close"
        />
      </div>

      <v-alert
        v-if="recipientMissing"
        type="warning"
        density="comfortable"
        class="mt-4"
      >
        {{ $t("ncrm.integrations.companyEmail.send.noRecipient") }}
      </v-alert>

      <v-alert
        v-if="senderError"
        type="warning"
        density="comfortable"
        class="mt-4"
      >
        <div class="d-flex align-center justify-space-between ga-3">
          <span>{{ senderError }}</span>
          <v-btn
            size="small"
            variant="text"
            :loading="senderLoading"
            @click="loadSenderPreview"
          >
            <v-icon start>mdi-refresh</v-icon>
            {{ $t("app.actions.refresh") }}
          </v-btn>
        </div>
      </v-alert>

      <v-progress-linear v-if="senderLoading" indeterminate color="primary" class="mt-4" />

      <div class="ncrm-company-email-send-drawer__meta mt-4">
        <div>
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.integrations.companyEmail.send.fields.to") }}
          </div>
          <div class="text-body-2">{{ draft.to || $t("ncrm.integrations.companyEmail.send.emptyValue") }}</div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.integrations.companyEmail.send.fields.from") }}
          </div>
          <div class="d-flex align-center flex-wrap ga-2">
            <span class="text-body-2">
              {{ senderLabel || $t("ncrm.integrations.companyEmail.send.senderLoading") }}
            </span>
            <v-chip v-if="senderPreview" size="x-small" color="primary">
              {{ senderSourceLabel }}
            </v-chip>
          </div>
        </div>
      </div>

      <v-form class="mt-5" @submit.prevent="submit">
        <v-text-field
          v-model="draft.to"
          type="email"
          autocomplete="email"
          :label="$t('ncrm.integrations.companyEmail.send.fields.to')"
          :placeholder="$t('ncrm.integrations.companyEmail.send.placeholders.to')"
          :disabled="submitting"
        />
        <v-text-field
          v-model="draft.subject"
          :label="$t('ncrm.integrations.companyEmail.send.fields.subject')"
          :placeholder="$t('ncrm.integrations.companyEmail.send.placeholders.subject')"
          :disabled="submitting"
        />
        <NcrmMailjetTemplatesTab v-if="canManagePurposes" purpose-only privacy-only :purpose-link="false"
          @purpose-activated="purposeCatalogRevision += 1" />
        <NcrmEmailPurposeSelect :key="purposeCatalogRevision" v-model="draft.purposeId" :disabled="submitting" />
        <!-- GDPR S7.3b: language of the marketing preference footer/link; defaults to the sender's UI language. -->
        <v-select
          v-model="preferenceLocale"
          :items="preferenceLocales"
          :label="$t('ncrm.integrations.companyEmail.send.fields.preferenceLocale')"
          :hint="$t('ncrm.integrations.companyEmail.send.preferenceLocaleHint')"
          persistent-hint
          :menu-props="{ zIndex: 3210 }"
          :disabled="submitting"
          class="mb-4"
        />
        <v-textarea
          v-model="draft.text"
          auto-grow
          rows="10"
          :label="$t('ncrm.integrations.companyEmail.send.fields.body')"
          :placeholder="$t('ncrm.integrations.companyEmail.send.placeholders.body')"
          :disabled="submitting"
        />
      </v-form>

      <v-alert v-if="formError" type="error" density="comfortable" class="mt-2">
        {{ formError }}
      </v-alert>

      <div class="d-flex justify-end ga-2 mt-auto pt-5">
        <v-btn variant="text" :disabled="submitting" @click="close">
          {{ $t("ncrm.integrations.companyEmail.send.actions.cancel") }}
        </v-btn>
        <v-btn color="primary" :loading="submitting" :disabled="!canSubmit" @click="submit">
          <v-icon start>mdi-email-fast-outline</v-icon>
          {{ $t("ncrm.integrations.companyEmail.send.actions.send") }}
        </v-btn>
      </div>
        </div>
      </aside>
    </div>
  </Teleport>
</template>

<script>
import axios from "axios";

import {
  buildCompanyEmailActivityPayload,
  buildCompanyEmailSendPayload,
  createCompanyEmailDraft,
  fetchCompanyEmailSenderPreview,
  formatCompanyEmailSender,
  isCompanyEmailDraftValid,
  normalizeCompanyEmailSenderPreview,
  sendCompanyEmail,
} from "@/Utile/companyEmailSend.mjs";
import NcrmEmailPurposeSelect from "@/components/ncrm/shared/NcrmEmailPurposeSelect.vue";
import NcrmMailjetTemplatesTab from "@/components/ncrm/integrations/mailjet/NcrmMailjetTemplatesTab.vue";
import { canAccessMailjetWorkspace } from "@/Utile/ncrmMitpSync.mjs";

const trimText = (value) => String(value ?? "").trim();

export default {
  name: "NcrmCompanyEmailSendDialog",
  components: { NcrmEmailPurposeSelect, NcrmMailjetTemplatesTab },
  emits: ["update:modelValue", "notify", "sent"],
  props: {
    modelValue: { type: Boolean, default: false },
    entityType: { type: String, required: true },
    entityId: { type: [String, Number], required: true },
    recipientEmail: { type: String, default: "" },
    recipientName: { type: String, default: "" },
  },
  data() {
    return {
      draft: createCompanyEmailDraft(),
      senderPreview: null,
      senderLoading: false,
      senderError: "",
      formError: "",
      submitting: false,
      purposeCatalogRevision: 0,
      preferenceLocale: "ro",
      // Language names are shown in their own language on purpose, like a language switcher.
      preferenceLocales: [{ value: "ro", title: "Română" }, { value: "en", title: "English" }, { value: "ru", title: "Русский" }],
    };
  },
  computed: {
    canManagePurposes() { return canAccessMailjetWorkspace(); },
    recipientMissing() {
      return !trimText(this.recipientEmail);
    },
    senderLabel() {
      return formatCompanyEmailSender(this.senderPreview?.sender || {});
    },
    senderSourceLabel() {
      const source = this.senderPreview?.source || "company_default";
      const labels = {
        company_default: "ncrm.integrations.companyEmail.send.senderSources.companyDefault",
        user_company_default: "ncrm.integrations.companyEmail.send.senderSources.userCompanyDefault",
        user_own_smtp: "ncrm.integrations.companyEmail.send.senderSources.userOwnSmtp",
      };
      return this.$t(labels[source] || labels.company_default);
    },
    canSubmit() {
      return Boolean(
        !this.submitting
          && !this.senderLoading
          && !this.senderError
          && this.senderLabel
          && isCompanyEmailDraftValid(this.draft)
      );
    },
  },
  watch: {
    modelValue(value) {
      if (value) this.initializeDialog();
    },
    recipientEmail(value) {
      if (this.modelValue && !trimText(this.draft.to)) {
        this.draft.to = trimText(value);
      }
    },
  },
  methods: {
    handleModelUpdate(value) {
      if (!value && this.submitting) return;
      this.$emit("update:modelValue", value);
    },
    close() {
      if (this.submitting) return;
      this.$emit("update:modelValue", false);
    },
    initializeDialog() {
      this.draft = createCompanyEmailDraft({ to: this.recipientEmail });
      const uiLocale = String(this.$i18n?.locale || "").slice(0, 2);
      this.preferenceLocale = ["ro", "en", "ru"].includes(uiLocale) ? uiLocale : "ro";
      this.senderPreview = null;
      this.senderError = "";
      this.formError = "";
      this.loadSenderPreview();
    },
    async loadSenderPreview() {
      if (!this.modelValue) return;
      this.senderLoading = true;
      this.senderError = "";
      try {
        const { data } = await fetchCompanyEmailSenderPreview();
        this.senderPreview = normalizeCompanyEmailSenderPreview(data || {});
      } catch (error) {
        this.senderPreview = null;
        this.senderError = error?.response?.data?.error
          || this.$t("ncrm.integrations.companyEmail.send.errors.loadSender");
      } finally {
        this.senderLoading = false;
      }
    },
    validateDraft() {
      if (!trimText(this.draft.to)) {
        this.formError = this.$t("ncrm.integrations.companyEmail.send.validation.toRequired");
        return false;
      }
      if (!trimText(this.draft.subject)) {
        this.formError = this.$t("ncrm.integrations.companyEmail.send.validation.subjectRequired");
        return false;
      }
      if (!trimText(this.draft.text)) {
        this.formError = this.$t("ncrm.integrations.companyEmail.send.validation.bodyRequired");
        return false;
      }
      if (!Number(this.draft.purposeId)) {
        this.formError = this.$t("ncrm.integrations.companyEmail.send.validation.purposeRequired");
        return false;
      }
      if (!this.senderLabel) {
        this.formError = this.senderError || this.$t("ncrm.integrations.companyEmail.send.errors.noSender");
        return false;
      }
      this.formError = "";
      return true;
    },
    async submit() {
      if (!this.validateDraft()) return;
      this.submitting = true;
      const normalizedDraft = createCompanyEmailDraft(this.draft);
      try {
        const { data: sendResult } = await sendCompanyEmail(buildCompanyEmailSendPayload(normalizedDraft,
          { preferenceLocale: this.preferenceLocale }));
        const activity = await this.createSentActivity({ draft: normalizedDraft, sendResult: sendResult || {} });
        this.$emit("notify", {
          message: activity
            ? this.$t("ncrm.integrations.companyEmail.send.alerts.successWithActivity")
            : this.$t("ncrm.integrations.companyEmail.send.alerts.success"),
          color: "success",
        });
        this.$emit("sent", { sendResult: sendResult || {}, activity });
        this.$emit("update:modelValue", false);
      } catch (error) {
        console.error("[ncrm] send company email failed:", error);
        this.formError = error?.response?.data?.error || this.$t("ncrm.integrations.companyEmail.send.errors.send");
      } finally {
        this.submitting = false;
      }
    },
    async createSentActivity({ draft, sendResult }) {
      try {
        const now = new Date().toISOString();
        const payload = buildCompanyEmailActivityPayload({
          entityType: this.entityType,
          entityId: this.entityId,
          draft,
          sendResult,
          sentAt: now,
        });
        const { data } = await axios.post("/ncrm/activity", payload);
        return data || payload;
      } catch (error) {
        console.error("[ncrm] save sent email activity failed:", error);
        this.$emit("notify", {
          message: error?.response?.data?.error
            || this.$t("ncrm.integrations.companyEmail.send.errors.activityLog"),
          color: "warning",
        });
        return null;
      }
    },
  },
};
</script>

<style>
.ncrm-company-email-send-drawer__overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  display: flex;
  justify-content: flex-end;
  background: rgba(16, 24, 40, 0.32);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 3200 !important;
}

.ncrm-company-email-send-drawer {
  width: min(520px, 100vw);
  height: 100%;
  background: rgb(var(--v-theme-surface));
  box-shadow: -16px 0 40px rgba(15, 23, 42, 0.18);
  z-index: 3201 !important;
}
</style>

<style scoped>
.ncrm-company-email-send-drawer__content {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.ncrm-company-email-send-drawer__meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
